/**
 * The listing wizard swaps steps with *ngIf inside ion-content.
 * ion-content keeps its scroll offset, so the next step opens at the bottom.
 * Scroll the wizard's own ion-content (create) or the ancestor one (edit, inside profile).
 */

const WIZARD_CONTENT_ID = 'create-offer-content';

export type ListingWizardScrollTarget = {
    scrollToTop: (duration?: number) => Promise<void> | void;
};

export type ListingWizardScrollHost = {
    querySelector?: (selector: string) => unknown;
    closest?: (selector: string) => unknown;
};

export type ListingWizardScrollSchedule = (callback: () => void) => void;

function isScrollTarget(value: unknown): value is ListingWizardScrollTarget {
    return !!value && typeof (value as ListingWizardScrollTarget).scrollToTop === 'function';
}

export function findListingWizardScrollTarget(host: ListingWizardScrollHost | null | undefined): ListingWizardScrollTarget | null {
    const own = host?.querySelector?.(`#${WIZARD_CONTENT_ID}`) ?? null;
    if (isScrollTarget(own)) {
        return own;
    }
    const ancestor = host?.closest?.('ion-content') ?? null;
    if (isScrollTarget(ancestor)) {
        return ancestor;
    }
    return null;
}

function blurActiveControl(): void {
    if (typeof document === 'undefined') {
        return;
    }
    const active = document.activeElement as { blur?: () => void } | null;
    if (!active || active === document.body || typeof active.blur !== 'function') {
        return;
    }
    active.blur();
}

function scrollWindowToTop(): void {
    if (typeof document !== 'undefined' && document.scrollingElement) {
        document.scrollingElement.scrollTop = 0;
    }
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        window.scrollTo(0, 0);
    }
}

function applyListingWizardScroll(host: ListingWizardScrollHost | null | undefined): void {
    blurActiveControl();
    const target = findListingWizardScrollTarget(host);
    if (!target) {
        scrollWindowToTop();
        return;
    }
    try {
        void Promise.resolve(target.scrollToTop(0)).catch((): void => undefined);
    } catch {
        scrollWindowToTop();
    }
}

function deferUntilAfterRender(callback: () => void): void {
    setTimeout(callback, 0);
}

/**
 * Scroll once now and once after the new step is in the DOM.
 * The second pass wins if the browser restores the focused Suivant button before Angular removes it.
 * Returns false when the step did not change, so wizard data and scroll stay as they are.
 */
export function scrollListingWizardOnStepChange(
    previousStep: string,
    nextStep: string,
    host: ListingWizardScrollHost | null | undefined,
    schedule: ListingWizardScrollSchedule = deferUntilAfterRender,
): boolean {
    if (previousStep === nextStep) {
        return false;
    }
    applyListingWizardScroll(host);
    schedule(() => applyListingWizardScroll(host));
    return true;
}
